import { subtitles, categories, type Subtitle, type CategoryInfo } from "@/mocks/subtitles";

export const allSubtitles: Subtitle[] = subtitles;
export const allCategories: CategoryInfo[] = categories;

export function getCategoryBySlug(slug: string): CategoryInfo | undefined {
  return categories.find((item) => item.slug === slug);
}

export function getSubtitleById(id: string): Subtitle | undefined {
  return subtitles.find((item) => item.id === id);
}

export function formatDownloads(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)} 万`;
  }
  return `${value}`;
}

export function searchSubtitles(keyword: string): Subtitle[] {
  const word = keyword.trim().toLowerCase();
  if (!word) return [];
  return subtitles.filter((item) => {
    const haystack = [
      item.title,
      item.originalTitle,
      item.type,
      String(item.year),
      item.tags.join(" "),
      item.summary,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(word);
  });
}

export function getRelatedSubtitles(item: Subtitle, count = 4): Subtitle[] {
  const scored = subtitles
    .filter((other) => other.id !== item.id)
    .map((other) => {
      let score = 0;
      if (other.typeSlug === item.typeSlug) score += 3;
      if (other.year === item.year) score += 1;
      score += other.tags.filter((tag) => item.tags.includes(tag)).length * 2;
      return { other, score };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((entry) => entry.other);
}

export function sortSubtitles(list: Subtitle[], sortKey: string): Subtitle[] {
  const clone = [...list];
  if (sortKey === "latest") {
    return clone.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }
  if (sortKey === "hot") {
    return clone.sort((a, b) => b.downloads - a.downloads);
  }
  if (sortKey === "rating") {
    return clone.sort((a, b) => b.rating - a.rating);
  }
  if (sortKey === "year") {
    return clone.sort((a, b) => b.year - a.year);
  }
  return clone;
}

export interface SubtitleFilters {
  language: string;
  format: string;
  period: string;
}

export const defaultFilters: SubtitleFilters = {
  language: "all",
  format: "all",
  period: "all",
};

export const languageOptions = [
  { value: "all", label: "全部语言" },
  { value: "简体中文", label: "简体中文" },
  { value: "繁体中文", label: "繁体中文" },
  { value: "中英双语", label: "中英双语" },
];

export const formatOptions = [
  { value: "all", label: "全部格式" },
  { value: "srt", label: "SRT" },
  { value: "ass", label: "ASS" },
];

export const periodOptions = [
  { value: "all", label: "全部时间" },
  { value: "7", label: "近 7 天" },
  { value: "30", label: "近 30 天" },
  { value: "180", label: "近半年" },
];

export const sortOptions = [
  { value: "latest", label: "最新更新" },
  { value: "hot", label: "最热下载" },
  { value: "rating", label: "评分最高" },
  { value: "year", label: "年份最新" },
];

const TODAY = new Date("2026-10-12T00:00:00");

function daysSince(dateText: string): number {
  const target = new Date(`${dateText}T00:00:00`);
  return Math.floor((TODAY.getTime() - target.getTime()) / 86400000);
}

export function applyFilters(list: Subtitle[], filters: SubtitleFilters): Subtitle[] {
  return list.filter((item) => {
    if (filters.language !== "all" && item.language !== filters.language) return false;
    if (filters.format !== "all" && item.format !== filters.format) return false;
    if (filters.period !== "all" && daysSince(item.updatedAt) > Number(filters.period)) return false;
    return true;
  });
}